/* ============================================================================
   product.js - variant picker + media gallery for the product page.

   The picker is a row of buttons per option. Most of the catalog has one row, a
   Buckle or a Caliber, where each button is one variant. A family the dropship
   app added as one product has two, Size and Color, and the variant is the one
   both pressed buttons carry. Pressing a value updates the form's variant id,
   the price block, each row's selected-value echo and the strike on every
   button, and jumps the gallery to that variant's media. Clicking a thumbnail
   shows that media item.

   What a press does with a value depends on the stock behind it:

     - In stock with what the other rows say: that variant, as asked.
     - In stock only with other choices: struck, but it answers. The other rows
       move to the in-stock variant that keeps the most of what they said, the
       first of those in the product's order when two keep as many. Refusing
       instead can leave a variant nobody can reach: Large Black pressed, Large
       Coyote and Small Black gone, and Small Coyote in stock behind two
       refusals.
     - Sold out in every combination: struck, and it does nothing.

   So a press never points the form at a sold-out variant, which matters because
   nothing in this file re-enables the add button. The page can still open on
   one, from a link that names it: the section renders the variant it was asked
   for, and an add of it comes back refused, which cart.js reports beside the
   button.

   DOM contract (within [data-product]):
     [data-option]                 one row per option, in the product's order
       [data-option-selected]      echo of the value pressed in the row
       [data-option-value]         one button per value, holding the value. Also
         data-option-variants: the ids of every variant with that value,
         space-separated, in the product's order. The section renders one
         sold out in every combination struck and aria-disabled, and this file
         never lifts either, because stock does not move while the page is open
     [data-variant]                one inert node per variant. Attributes:
        data-variant-id, data-variant-available ("true"|"false"),
        data-variant-price (formatted), data-variant-compare (formatted|"") -
          both ABSENT when MAP-gated, so there is no price in the markup at all,
        data-variant-media-id (media id|"")
     input[name="id"]              the variant id the form submits
     [data-price-current]          price text (skipped when [data-map-gated])
     [data-price-compare]          compare-at text (hidden when none)
     [data-add-to-cart]            add button. Present only on an available product;
       main-product.liquid renders a separate, attribute-less Sold Out button
       otherwise. This file does not touch its disabled state - cart.js owns that
       while an add is in flight, and there is no sold-out variant to reflect,
       because a press never selects one.
     [data-map-gated]              present when MAP-gated (no price reveal)

   Gallery (product-gallery.liquid, optional - absent for placeholder plates):
     [data-media-item]       one per media; data-media-id. Non-active ones hidden.
     [data-gallery-thumb]    one per media; data-media-target = a media id.
   ========================================================================== */
(function () {
  "use strict";

  // Value-button state - mirrors the server-rendered `cls` in main-product.liquid
  // (selected has no hover; unselected carries the hover affordance) so the tandem
  // doesn't strand hover classes after a click.
  var SELECTED = ["border-stone", "bg-chalk/10", "text-bone"];
  var UNSELECTED = ["border-line", "text-bone-dim", "hover:border-line-strong", "hover:text-bone"];
  // The strike on a value with no variant in stock beside what the other rows say.
  // It moves with them, so the page cannot render it and this file paints it. A
  // value sold out in every combination is struck wherever the rows stand, and the
  // section's `oos` also takes the pointer from it and marks it aria-disabled.
  // Neither of those can change while the page is open, so they are left alone.
  var STRUCK = ["line-through", "opacity-40"];

  function swap(el, on, off) {
    el.classList.remove.apply(el.classList, off);
    el.classList.add.apply(el.classList, on);
  }

  function init(rootEl) {
    var mediaItems = rootEl.querySelectorAll("[data-media-item]");
    var thumbs = rootEl.querySelectorAll("[data-gallery-thumb]");

    // ── Gallery: show one media item, sync thumbnail state ──────────────────
    function showMedia(id) {
      if (!id) return;
      var found = false;
      mediaItems.forEach(function (item) {
        if (item.getAttribute("data-media-id") === id) found = true;
      });
      if (!found) return; // unknown id (e.g. variant with no media) - leave the stage
      mediaItems.forEach(function (item) {
        var on = item.getAttribute("data-media-id") === id;
        var wasVisible = !item.classList.contains("hidden");
        item.classList.toggle("hidden", !on);
        // stop playback when hiding what was on screen: native <video> and, for
        // YouTube/Vimeo embeds, reloading the iframe src (which halts its audio).
        if (!on && wasVisible) {
          item.querySelectorAll("video").forEach(function (v) { v.pause(); });
          item.querySelectorAll("iframe").forEach(function (f) { f.src = f.src; });
        }
      });
      thumbs.forEach(function (t) {
        var on = t.getAttribute("data-media-target") === id;
        t.setAttribute("aria-pressed", on ? "true" : "false");
        if (on) { t.classList.add("border-stone"); t.classList.remove("border-line", "hover:border-line-strong"); }
        else { t.classList.remove("border-stone"); t.classList.add("border-line", "hover:border-line-strong"); }
      });
    }
    thumbs.forEach(function (t) {
      t.addEventListener("click", function () { showMedia(t.getAttribute("data-media-target")); });
    });

    // ── Variant picker ──────────────────────────────────────────────────────
    var rows = Array.prototype.map.call(rootEl.querySelectorAll("[data-option]"), function (row) {
      return {
        echo: row.querySelector("[data-option-selected]"),
        values: Array.prototype.map.call(row.querySelectorAll("[data-option-value]"), function (btn) {
          var ids = (btn.getAttribute("data-option-variants") || "").split(" ");
          return { btn: btn, ids: ids.filter(Boolean) };
        }),
      };
    });
    if (!rows.length) return;
    var variants = {};
    rootEl.querySelectorAll("[data-variant]").forEach(function (node) {
      variants[node.getAttribute("data-variant-id")] = node;
    });
    var idInput = rootEl.querySelector('input[name="id"]');
    var priceCurrent = rootEl.querySelector("[data-price-current]");
    var priceCompare = rootEl.querySelector("[data-price-compare]");
    var mapGated = !!rootEl.querySelector("[data-map-gated]");

    // The value pressed in each row, as the page rendered it: the variant it opened on.
    var chosen = rows.map(function (row) {
      return row.values.filter(function (value) {
        return value.btn.getAttribute("aria-pressed") === "true";
      })[0] || null;
    });

    function inStock(id) {
      var node = variants[id];
      return !!node && node.getAttribute("data-variant-available") === "true";
    }
    /** How many rows other than row `p` have a value pressed that `id` carries; -1 counts them all. */
    function kept(id, p) {
      return chosen.filter(function (value, q) {
        return q !== p && value !== null && value.ids.indexOf(id) >= 0;
      }).length;
    }

    function paint() {
      rows.forEach(function (row, p) {
        var pressed = chosen[p];
        if (row.echo) row.echo.textContent = pressed ? pressed.btn.getAttribute("data-option-value") : "";
        row.values.forEach(function (value) {
          var btn = value.btn;
          var on = value === pressed;
          // In stock beside every other row's pressed value.
          var here = value.ids.some(function (id) { return inStock(id) && kept(id, p) === rows.length - 1; });
          btn.setAttribute("aria-pressed", on ? "true" : "false");
          swap(btn, on ? SELECTED : UNSELECTED, on ? UNSELECTED : SELECTED);
          STRUCK.forEach(function (k) { btn.classList.toggle(k, !here); });
        });
      });
    }

    function select(id) {
      chosen = rows.map(function (row) {
        return row.values.filter(function (value) { return value.ids.indexOf(id) >= 0; })[0] || null;
      });
      paint();

      var node = variants[id];
      if (idInput) idInput.value = id;

      if (!mapGated && priceCurrent) {
        var p = node.getAttribute("data-variant-price");
        if (p) priceCurrent.textContent = p;
        if (priceCompare) {
          var c = node.getAttribute("data-variant-compare");
          if (c) { priceCompare.textContent = c; priceCompare.removeAttribute("hidden"); }
          else priceCompare.setAttribute("hidden", "");
        }
      }

      showMedia(node.getAttribute("data-variant-media-id"));
      // No `addBtn.disabled = false` here. It was half of cart.js's setBusy: click
      // Add, then pick a different variant while the request is out, and the button
      // came back enabled while still carrying aria-busy="true" - a control that is
      // clickable and announces itself as working at the same time. Nothing in this
      // file ever disables it, so nothing in this file has any business enabling it.
    }

    function press(p, value) {
      var best = null;
      var most = -1;
      value.ids.forEach(function (id) {
        if (!inStock(id)) return;
        var k = kept(id, p);
        if (k > most) { best = id; most = k; }
      });
      if (best !== null) select(best);
    }

    rows.forEach(function (row, p) {
      row.values.forEach(function (value) {
        value.btn.addEventListener("click", function (e) { e.preventDefault(); press(p, value); });
      });
    });

    // The form posts what the rows show, from before the first press. The page
    // renders the field and the pressed buttons from one variant, but Firefox (96
    // on) puts back a hidden field's value on reload when a script set it. A
    // shopper who picked Coyote and pressed F5 would see Black pressed and post Coyote.
    Object.keys(variants).forEach(function (id) {
      if (idInput && kept(id, -1) === rows.length) idInput.value = id;
    });
    // And the in-stock-elsewhere marks, which the page cannot render because they
    // depend on the other rows.
    paint();
  }

  function boot() {
    document.querySelectorAll("[data-product]").forEach(init);
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
})();
